'use client';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/components/cms/types/blok-component';

import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';
import Button from '@/decodo/components/atoms/Button';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/decodo/components/atoms/Text';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';
import { useShowMoreLess } from '@/features/utils/useShowMoreLess';

interface TextGridStrapiProps {
  header?: BlockHeader;
  items: {
    image?: ImageStrapiProps;
    title: string;
    description: RichTextFieldStrapiProps;
    link?: LinkButtonStrapiProps;
  }[];
  layout: 'two-columns' | 'three-columns';
  isShowMoreVisible: boolean;
}

export function TextGridStrapi({
  block,
  ...rest
}: BlockComponentModel<TextGridStrapiProps>) {
  const { items, header, layout, isShowMoreVisible } = block;

  const { toggleShowMore, initiallyVisibleItems, buttonText, showAction } =
    useShowMoreLess({
      items,
      initialShowCount: isShowMoreVisible
        ? layout === 'two-columns'
          ? 4
          : 6
        : Infinity,
    });
  const cardsClasses = {
    'two-columns': 'grid grid-cols-1 md:grid-cols-2',
    'three-columns': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  };

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.TEXT_GRID}>
      <div>
        <BlockHeaderContent
          {...header}
          renderButtons={
            showAction
              ? [
                  (props) => (
                    <Button
                      onClick={toggleShowMore}
                      key={buttonText + 'text-grid-strapi'}
                      {...props}
                      variant='outlined'
                      color='secondary'
                    >
                      {buttonText}
                    </Button>
                  ),
                ]
              : undefined
          }
          classNames={{
            children: cn(
              'gap-8 md:gap-[3.75rem] lg:gap-20 md:items-start items-start',
              cardsClasses[layout]
            ),
          }}
        >
          {items.map((item, index) => (
            <div
              className={cn(
                'text-left',
                initiallyVisibleItems?.length &&
                  !initiallyVisibleItems.some(
                    (card) => card.title === item.title
                  ) &&
                  'hide-content'
              )}
              key={index}
            >
              {item.image && (
                <div className='mb-6'>
                  <ImageStrapi
                    block={item.image}
                    inner={{ classNames: { image: 'w-9' } }}
                  />
                </div>
              )}
              <Text
                variant='subtitle-l-d'
                color='base-black'
                classNames={{ wrapper: 'mb-6 font-serif' }}
              >
                {item.title}
              </Text>
              <div className='flex flex-wrap gap-2 text-gray-500'>
                <div data-tracker-component='description'>
                  <RichTextFieldStrapi block={item.description} />
                </div>
                {item.link && (
                  <LinkButtonStrapi
                    data-tracker-component='learn-more'
                    block={item.link}
                    inner={{
                      variant: 'text',
                      slim: true,
                      classNames: { root: 'text-tertiary-500' },
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
